/**
 * Geometria do cortador Holmatro: pivô e contornos das lâminas e da cabeça.
 *
 * As lâminas são desenhadas no referencial do pivô (pivô na origem, x para a
 * frente), com abertura zero. O cortador anima de 0,72 rad (aberto) a 0,08 rad
 * (corte concluído), e a cena posiciona o ponto de corte a ~0,62 m da origem
 * da ferramenta: o pivô em x = 0,38 e o bico a ~0,24 dele mantêm isso.
 *
 * Diferente do alargador, o cortador fechado **cruza** as lâminas, como uma
 * tesoura: o gume de cada uma passa do eixo, e é esse cruzamento que corta.
 * Elas não colidem porque ficam em planos z diferentes. O braço inferior é o
 * espelho do superior em y.
 */
import { arco, curva, type Ponto2 } from '../componentes/contornos2d'

/** Pivô das lâminas, no referencial do cortador. */
export const PIVO_CORTADOR: Ponto2 = [0.38, 0]

const RAIO_CUBO = 0.05
const RAIZ_GUME: Ponto2 = [0.035, -0.012]
const FIM_GUME: Ponto2 = [0.17, -0.016]
/** Bico em garra: curva para baixo, para abraçar a coluna sem escorregar. */
export const BICO_LAMINA: Ponto2 = [0.235, -0.045]

/**
 * Gume da lâmina superior, da raiz ao bico. Fica todo abaixo do eixo, com uma
 * barriga leve: assim, no fim do curso (0,08 rad), os gumes das duas lâminas
 * já se cruzaram em toda a extensão, como uma tesoura fechada. Côncavo demais,
 * o cortador "fechado" mostrava um vão em O entre as lâminas.
 */
export function gumeLamina(passos = 10): Ponto2[] {
  return [
    RAIZ_GUME,
    ...curva(RAIZ_GUME, [0.1, -0.004], FIM_GUME, passos),
    ...curva(FIM_GUME, [0.215, -0.02], BICO_LAMINA, Math.max(2, Math.round(passos / 2))),
  ]
}

/** Lâmina superior: gume côncavo, bico em garra, dorso curvo e cubo do pivô. */
export function contornoLamina(passos = 10): Ponto2[] {
  const anguloTopoCubo = Math.PI / 3
  const topoCubo: Ponto2 = [RAIO_CUBO * Math.cos(anguloTopoCubo), RAIO_CUBO * Math.sin(anguloTopoCubo)]
  const fimCubo = Math.PI * 1.39
  const baseCubo: Ponto2 = [RAIO_CUBO * Math.cos(fimCubo), RAIO_CUBO * Math.sin(fimCubo)]
  const costasBico: Ponto2 = [0.215, 0.045]
  return [
    ...gumeLamina(passos),
    ...curva(BICO_LAMINA, [0.262, 0.004], costasBico, Math.max(2, Math.round(passos / 2))),
    ...curva(costasBico, [0.15, 0.088], topoCubo, passos),
    ...arco([0, 0], RAIO_CUBO, anguloTopoCubo, fimCubo, passos),
    ...curva(baseCubo, [0.02, -0.035], RAIZ_GUME, Math.max(2, Math.round(passos / 2))).slice(0, -1),
  ]
}

/**
 * Faixa polida do gume: a borda afiada, um pouco para dentro da lâmina.
 * É o que separa visualmente o fio do corpo forjado.
 */
export function contornoFaixaGume(passos = 10, largura = 0.007): Ponto2[] {
  const gume = gumeLamina(passos)
  const deslocado = gume.map((ponto, i) => {
    const anterior = gume[Math.max(0, i - 1)]
    const seguinte = gume[Math.min(gume.length - 1, i + 1)]
    const dx = seguinte[0] - anterior[0]
    const dy = seguinte[1] - anterior[1]
    const comprimento = Math.hypot(dx, dy) || 1
    // à esquerda de quem anda da raiz para o bico fica o corpo da lâmina
    return [ponto[0] - (dy / comprimento) * largura, ponto[1] + (dx / comprimento) * largura] as Ponto2
  })
  return [...gume, ...deslocado.reverse()]
}

/**
 * Placa lateral da cabeça, no referencial do cortador: envolve o pivô e se
 * abre em abas defletoras atrás das lâminas, que seguram estilhaço.
 */
export function contornoCabeca(passos = 8): Ponto2[] {
  const [px, py] = PIVO_CORTADOR
  const raio = 0.055
  return [
    [0.25, -0.05],
    [0.3, -0.085],
    [0.336, -0.07],
    ...arco([px, py], raio, -1.2, 1.2, passos),
    [0.336, 0.07],
    [0.3, 0.085],
    [0.25, 0.05],
  ]
}
